<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, shallowRef } from "vue"
import { useI18n } from "vue-i18n"
import { useConfigStore } from "@core/stores/config.store"

import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"

// ================================
// TYPES & INTERFACES
// ================================
/** Instance exposée par `ItemAlarm` pour le toggle global. */
type ItemAlarmExposed = InstanceType<
    typeof import("../item/ItemAlarm.vue").default
>

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                activateAllAlarms: "Activer toutes les alarmes",
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    type: "building" | "source"
    itemId: string
    item: {
        profil?: Record<string, ThresholdPoint[]>
        alarme?: Record<
            string,
            Record<string, boolean | number | string | undefined>
        >
    } | null
}>()

// ================================
// CONFIG VARIABLES & LIGNES
// ================================
const configStore = useConfigStore()

/**
 * Unité affichée sur le graphique seuils pour une variable métier.
 *
 * @param variableType identifiant `ConfigVariable.type`
 * @returns `ConfigVariable.unit` ou chaîne vide
 */
function getVariableUnit(variableType: string): string {
    return configStore.getVariableByType(variableType)?.unit ?? ""
}

const getVariables = computed(() => Object.keys(props.item?.profil ?? {}))
const rowRefs = shallowRef<Record<string, ItemAlarmExposed | null>>({})

/** True si chaque variable listée a une alarme avec `active` à true (données store / props). */
const getAllAlarmsActive = computed(() => {
    const variables = getVariables.value
    if (variables.length === 0) return false
    const alarme = props.item?.alarme
    if (!alarme) return false
    return variables.every((variable) => Boolean(alarme[variable]?.active))
})

function getAlarmConfig(
    variable: string
): Record<string, boolean | number | string> | undefined {
    const alarm = props.item?.alarme?.[variable]
    if (!alarm) return undefined

    return Object.fromEntries(
        Object.entries(alarm).filter(([, value]) => value !== undefined)
    ) as Record<string, boolean | number | string>
}

/**
 * Enregistre l’instance `ItemAlarm` pour le toggle global.
 * Ne met à jour `rowRefs` que si l’instance change : sinon chaque re-render
 * du fils (ex. après patch `alarme` dans le store) rappelle la ref fonction,
 * ce qui réassignait `rowRefs`, relançait le parent et provoquait une boucle
 * avec `<transition-group>` (« Maximum recursive updates »).
 */
function onRowComponentRef(variable: string, el: unknown) {
    if (!el) {
        if (variable in rowRefs.value) {
            const next = { ...rowRefs.value }
            delete next[variable]
            rowRefs.value = next
        }
        return
    }
    const exposed = el as ItemAlarmExposed
    if (rowRefs.value[variable] === exposed) {
        return
    }
    rowRefs.value = {
        ...rowRefs.value,
        [variable]: exposed,
    }
}

function onToggleAll(value: boolean) {
    Object.values(rowRefs.value).forEach((row) =>
        row?.updateAlarmToggle?.(value)
    )
}
</script>

<template>
    <div class="alarm-variable-list flex flex-col gap-3">
        <BaseInputWrapper
            v-if="getVariables.length"
            input-id="alarm-activate-all"
            :label="t('alarm.activateAllAlarms')"
            unfloat
            orientation="horizontal"
            class="ml-4"
        >
            <BaseToggleSwitch
                :model-value="getAllAlarmsActive"
                @update:model-value="onToggleAll(Boolean($event))"
            />
        </BaseInputWrapper>
        <transition-group name="fade" tag="div" class="flex flex-col gap-2">
            <ItemAlarm
                v-for="variable in getVariables"
                :key="variable"
                :ref="(el) => onRowComponentRef(variable, el)"
                :type="type"
                :item-id="itemId"
                :variable="variable"
                :variable-unit="getVariableUnit(variable)"
                :points="item?.profil?.[variable] ?? []"
                :alarm="getAlarmConfig(variable)"
            />
        </transition-group>
    </div>
</template>
